import React, { useEffect, useState } from 'react';
import MasterLayout from '../../../components/AppLayout/MasterLayout';
import useRouter from '../../../utils/useRouter';
import { Box, Button, Stack, SvgIcon } from '@mui/material';
import './coursePlayerPage.scss';
import { routes } from '../../../constants';
import { updateCourseRegistration as updateCourseRegistrationAction } from '../../../actions/course.actions';
import useAction from '../../../utils/useAction';
import courseService from '../../../services/course.service';
import CoursePlayerPageLoader from './CoursePlayerPageLoader';
import { fetchMindItem as fetchMindItemAction } from '../../../actions/mindItems.actions';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { useSelector } from 'react-redux';
import { trackContentViewed } from '../../../utils/tracking';
import getMuiIcon, { IconName } from '../../../utils/getMuiIcon';
import { useTranslation } from 'react-i18next';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { AddMenuButton } from '../../../components/cards/MuiCard/MuiCardBase/MuiCardBase';
import { spaces } from '../../../assets/styles/themes/tokens';

const CoursePlayerPage = () => {
  const { t } = useTranslation();
  const { router, match } = useRouter();
  const [isFetching, setIsFetching] = useState(true);
  const [courseLaunchLink, setCourseLaunchLink] = useState<string | null>(null);
  const updateCourseRegistration = useAction(updateCourseRegistrationAction);
  const fetchCourseMindItem = useAction(fetchMindItemAction);
  const courseId = match.params.courseId;

  const mindItem = useSelector((state) => selectMindItem(state, courseId));

  useEffect(() => {
    fetchCourseMindItem(courseId);
  }, [courseId, fetchCourseMindItem]);

  useEffect(() => {
    if (!mindItem || courseLaunchLink) return;
    
    courseService.buildCourseLaunchLink(courseId)
      .then(({ launchLink }) => {
        setCourseLaunchLink(launchLink);
        trackContentViewed(mindItem, true, false);
      })
      .finally(() => setIsFetching(false));
  }, [mindItem, courseLaunchLink, courseId]);

  useEffect(() => {
    const handleMessages = (event: MessageEvent) => {
      switch (event.data.eventType) {
        case 'courseRegistration':
          updateCourseRegistration(event.data.courseRegistration);
          break;
        case 'relaunchCourse':
          setIsFetching(true);
          setCourseLaunchLink(null);
          break;
      }
    };
    window.addEventListener('message', handleMessages);

    return () => {
      window.removeEventListener('message', handleMessages);
    };
  }, [updateCourseRegistration]);

  useEffect(() => {
    if (!isFetching && !courseLaunchLink) {
      router.replace(`/${routes.courseCover.root}/${courseId}`);
    }
  }, [isFetching, courseLaunchLink, router, courseId]);

  const navigateToAttachmentsPage = () => {
    router.push(`/${routes.courseAttachments.root.replace(':courseId', courseId)}`);
  };

  const customTopBarContent = (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Breadcrumbs
        links={[
          {
            title: t('Search'),
            url: `/${routes.courseCatalog.root}`,
            iconName: 'searchDone' as IconName,
          },
          {
            title: t('Course overview'),
            url: `/${routes.courseCatalog.root}/${courseId}`,
            iconName: 'book' as IconName,
          },
          {
            title: mindItem?.title,
            iconName: 'docText' as IconName,
          }
        ]}
      />
      <Stack direction='row' alignItems='center' gap={spaces.xs}>
        {mindItem && (
          <AddMenuButton
            isPinned={!!mindItem.isPinned}
            id={courseId}
            itemData={mindItem}
            type='course'
            size='medium'
            buttonSx={{
              position: 'relative',
              top: 'unset',
              right: 0,
            }}
          />
        )}
        {mindItem && mindItem?.attachments.length > 0 && (
          <Button 
            onClick={navigateToAttachmentsPage}
            startIcon={
              <SvgIcon component={getMuiIcon('paperclip')} />
            }
            variant='outlined'
            size='medium'
            color='primary'
            sx={{
              flexShrink: 0
            }}
          >
            {mindItem.attachments.length} {t('Materials')}
          </Button>
        )}
      </Stack>
    </Stack>
  );

  return (
    <MasterLayout
      customTopBarContent={customTopBarContent}
      hideAvatar
      forceNewNavigation
    >
      {isFetching ? (
        <CoursePlayerPageLoader />
      ) : (
        courseLaunchLink && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
            }}
          >
            <iframe
              src={courseLaunchLink}
              className='course-player'
              data-testid='course-player'
            />
          </Box>
        )
      )}
    </MasterLayout>
  );
};

export default CoursePlayerPage;
